import { RangeHorizon, ResampleIntervals } from '@toggle/chart';
import { hasKeyValues } from '@toggle/helpers';
import { AssetSubClassCode, Entity } from '@toggle/toggle';
import React from 'react';

import { ErrorBoundaryWrapper } from '~/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { About } from '~/modules/about/About';
import { OverviewEventFeed } from '~/modules/overview-event-feed/OverviewEventFeed';
import { OverviewKeyValues } from '~/modules/overview-key-values/OverviewKeyValues';
import { RevampThemeKnowledgeGraph } from '~/modules/overview-knowledge-graph/components/theme-knowledge-graph/revamp-theme-knowledge-graph/RevampThemeKnowledgeGraph';
import { ThemeKnowledgeGraph } from '~/modules/overview-knowledge-graph/components/theme-knowledge-graph/ThemeKnowledgeGraph';
import { SimpleChart } from '~/modules/simple-chart/SimpleChart';
import { useCompanyTheme } from '~/react-query-hooks/knowledge-graph/useKnowledgeGraph';
import { useSnakeMeta } from '~/react-query-hooks/snakes/useSnake';
import { useFlags } from '~/stores/use-flags/useFlags';
import { useSubscription } from '~/stores/use-subscription/useSubscription';
import { ChartSettings } from '~/views/turbo-chart/utils/chart-settings-utils/chart-settings-utils';
import { useAssetOverviewNavigation } from '~/widgets/asset-overview-widget/hooks/use-asset-overview-navigation/useAssetOverviewNavigation';
import { BrandsAndProductsSection } from '~/widgets/knowledge-graph-widget/components/brands-and-products-section/BrandsAndProductsSection';
import { CompetitorsSection } from '~/widgets/knowledge-graph-widget/components/competitors-tab/tabs/competitor-detail/components/CompetitorsSection';
import { RevampCompetitorsSection } from '~/widgets/knowledge-graph-widget/components/competitors-tab/tabs/competitor-detail/components/revamp-competitors-section/RevampCompetitorsSection';
import { EarningsInsightSection } from '~/widgets/knowledge-graph-widget/components/earnings-insight-section/EarningsInsightSection';
import { useEarningsInsightsByEntity } from '~/widgets/knowledge-graph-widget/components/earnings-insight-section/useEarningsInsightsByEntity';
import { GeographicExposureSection } from '~/widgets/knowledge-graph-widget/components/geographic-exposure-tab/geographic-exposure-section/GeographicExposureSection';
import { RevampGeographicExposureSection } from '~/widgets/knowledge-graph-widget/components/geographic-exposure-tab/geographic-exposure-section/revamp-geographic-exposure-section/RevampGeographicExposureSection';
import { UpgradeToUnlock } from '~/widgets/overview/components/upgrade-to-unlock/UpgradeToUnlock';

import * as S from './AssetOverviewContent.styles';

const ERROR_404_STATUS = 404;

export const AssetOverviewContent = ({ entity }: { entity: Entity }) => {
  const { data: snakeMeta } = useSnakeMeta(entity?.default_snake);
  const { navigateToChart } = useAssetOverviewNavigation(entity);

  const isBasicOrLightUser = useSubscription(state =>
    state.isBasicOrLightUser()
  );
  const isStreamFeedV2Enabled = useFlags(state =>
    state.getFeatureFlagState('stream_feed_v2')
  );

  const isStock = entity.sub_class === AssetSubClassCode.EquityStock;
  const { error: companyThemeError } = useCompanyTheme({
    entityTag: entity.tag,
  });

  const {
    earningsData,
    isLoading: isEarningsLoading,
    earningsInsight,
  } = useEarningsInsightsByEntity(entity.tag);

  const chartSettings: ChartSettings = {
    assets: [
      {
        tag: entity.tag,
        snake: entity.default_snake,
        paneIndex: 0,
      },
    ],
    resample: ResampleIntervals.OneDay,
    horizon: RangeHorizon.ThreeMonths,
    domain: null,
    hidden: undefined,
    paneSettings: {},
  };

  const displayConnections =
    companyThemeError?.status !== ERROR_404_STATUS && isStock;

  return (
    <>
      <S.StyledEntityHeader
        entity={entity}
        truncateLongTitle
        truncateAtLength={19}
        isDrawer
        headerButton={<></>}
      />
      <S.ContentContainer>
        <S.ChartWrapper>
          <S.StyledChartPrice entity={entity} snakeMeta={snakeMeta} />
          <SimpleChart
            entity={entity}
            initialChartSettings={chartSettings}
            isLegacyVariant={false}
            onClickFullscreen={navigateToChart}
            maxHorizon={RangeHorizon.ThreeYears}
            eventsConfig={{
              TMI: true,
            }}
          />
        </S.ChartWrapper>

        {isStreamFeedV2Enabled ? (
          <>
            <EarningsInsightSection
              entity={entity}
              earningsData={earningsData}
              earningsInsight={earningsInsight}
              isLoading={isEarningsLoading}
              isPreview={earningsInsight?.type === 'preview'}
            />
            {isBasicOrLightUser ? (
              <UpgradeToUnlock />
            ) : (
              <ErrorBoundaryWrapper>
                <OverviewEventFeed entityTag={entity.tag} />
              </ErrorBoundaryWrapper>
            )}
            <About
              entity={entity}
              variant="secondary"
              showMoreDescription
              limit={300}
            />
            {displayConnections && (
              <>
                <RevampThemeKnowledgeGraph entityTag={entity.tag} isInDrawer />
                <RevampCompetitorsSection entityTag={entity.tag} isInDrawer />
                <RevampGeographicExposureSection
                  entityTag={entity.tag}
                  isInDrawer
                />
                <BrandsAndProductsSection entityTag={entity.tag} isInDrawer />
              </>
            )}
          </>
        ) : (
          <>
            <About entity={entity} isModal showMoreDescription />
            {hasKeyValues(entity.tag) && (
              <S.StyledOverviewKeyValuesWrapper>
                <ErrorBoundaryWrapper>
                  <OverviewKeyValues entityTag={entity.tag} />
                </ErrorBoundaryWrapper>
              </S.StyledOverviewKeyValuesWrapper>
            )}
            <ErrorBoundaryWrapper>
              <ThemeKnowledgeGraph entityTag={entity.tag} />
            </ErrorBoundaryWrapper>
            <ErrorBoundaryWrapper>
              <BrandsAndProductsSection entityTag={entity.tag} isInDrawer />
            </ErrorBoundaryWrapper>
            <CompetitorsSection entityTag={entity.tag} isInDrawer />
            <ErrorBoundaryWrapper>
              <GeographicExposureSection entityTag={entity.tag} isInDrawer />
            </ErrorBoundaryWrapper>
          </>
        )}
      </S.ContentContainer>
    </>
  );
};
